import React from "react";
import { Table, Tag } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined, SendOutlined, EyeOutlined, DeleteOutlined } from "@ant-design/icons";
import { numberWithCommas } from "utils/GlobalFunctions";
import moment from "moment";
import "./FacilityApplicationsTable.css";

function FacilityApplicationsTable(props) {
  const { dataList, onApprove, onReject, onResendLink, onView, onDelete, onEventDateClick } = props;

  if (dataList === undefined) return <div />;

  const statusColors = {
    pending: "orange",
    approved: "green",
    rejected: "red",
  };

  const formatDateShort = (value) => {
    if (!value) return "";
    const parsed = moment(value, ["YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss", moment.ISO_8601], true);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
  };

  const formatTime = (value) => {
    if (!value) return "";
    const parsed = moment(value, ["HH:mm:ss", "HH:mm", "hh:mm A", "h:mm A"], true);
    return parsed.isValid() ? parsed.format("hh:mm A") : value;
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
      fixed: "left",
      sorter: (a, b) => parseInt(a.id) - parseInt(b.id),
    },
    {
      title: "Applicant",
      dataIndex: "applicant_name",
      key: "applicant_name",
      width: 160,
      sorter: (a, b) => `${a.applicant_name}`.localeCompare(`${b.applicant_name}`),
    },
    {
      title: "Event Date",
      dataIndex: "event_date",
      key: "event_date",
      width: 110,
      sorter: (a, b) => `${a.event_date}`.localeCompare(`${b.event_date}`),
      render: (val) => (
        <a
          onClick={(e) => {
            e.stopPropagation();
            onEventDateClick && onEventDateClick(val);
          }}
        >
          {formatDateShort(val)}
        </a>
      ),
    },
    {
      title: "Start",
      dataIndex: "event_start_time",
      key: "event_start_time",
      width: 90,
      render: (val) => formatTime(val),
    },
    {
      title: "End",
      dataIndex: "event_end_time",
      key: "event_end_time",
      width: 90,
      render: (val) => formatTime(val),
    },
    {
      title: "Hours",
      dataIndex: "total_hours",
      key: "total_hours",
      width: 70,
      align: "center",
      sorter: (a, b) => parseFloat(a.total_hours || 0) - parseFloat(b.total_hours || 0),
    },
    {
      title: "Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      width: 100,
      sorter: (a, b) => parseFloat(a.total_amount || 0) - parseFloat(b.total_amount || 0),
      render: (val) => "$" + numberWithCommas(val),
    },
    {
      title: "Email",
      dataIndex: "contact_email",
      key: "contact_email",
      width: 200,
      sorter: (a, b) => `${a.contact_email || ""}`.localeCompare(`${b.contact_email || ""}`),
    },
    {
      title: "Phone",
      dataIndex: "contact_phone",
      key: "contact_phone",
      width: 130,
    },
    {
      title: "Alcohol",
      dataIndex: "alcohol",
      key: "alcohol",
      width: 90,
      render: (val) => {
        if (val === undefined || val === null || val === "") return "-";
        const normalized = `${val}`.toLowerCase();
        if (normalized === "1" || normalized === "yes" || normalized === "true") return "Yes";
        if (normalized === "0" || normalized === "no" || normalized === "false") return "No";
        return val;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      sorter: (a, b) => `${a.status}`.localeCompare(`${b.status}`),
      render: (status) => {
        const displayStatus = (status || "pending").toLowerCase();
        return (
          <Tag color={statusColors[displayStatus] || "default"}>
            {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
          </Tag>
        );
      },
    },
    {
      title: "Submitted",
      dataIndex: "created_at",
      key: "created_at",
      width: 110,
      sorter: (a, b) => `${a.created_at}`.localeCompare(`${b.created_at}`),
      render: (val) => formatDateShort(val),
    },
  ];

  // Add Actions column
  if (onApprove || onReject || onView || onDelete) {
    columns.push({
      title: "Actions",
      key: "actions",
      width: 320,
      fixed: "right",
      render: (_, record) => {
        const status = (record.status || "pending").toLowerCase();

        if (status === "pending") {
          return (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a
                style={{ color: "#1890ff" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onView && onView(record);
                }}
              >
                <EyeOutlined /> View
              </a>
              <a
                style={{ color: "#52c41a" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onApprove && onApprove(record);
                }}
              >
                <CheckCircleOutlined /> Approve
              </a>
              <a
                style={{ color: "#ff4d4f" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onReject && onReject(record);
                }}
              >
                <CloseCircleOutlined /> Reject
              </a>
              <a
                style={{ color: "#ff4d4f" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete && onDelete(record);
                }}
              >
                <DeleteOutlined /> Delete
              </a>
            </div>
          );
        }

        if (status === "approved") {
          return (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a
                style={{ color: "#1890ff" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onView && onView(record);
                }}
              >
                <EyeOutlined /> View
              </a>
              <Tag color="green">Approved</Tag>
              {onResendLink && (
                <a
                  style={{ color: "#1890ff" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onResendLink(record);
                  }}
                >
                  <SendOutlined /> Resend Link
                </a>
              )}
              <a
                style={{ color: "#ff4d4f" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete && onDelete(record);
                }}
              >
                <DeleteOutlined /> Delete
              </a>
            </div>
          );
        }

        if (status === "rejected") {
          return (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <a
                style={{ color: "#1890ff" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onView && onView(record);
                }}
              >
                <EyeOutlined /> View
              </a>
              <Tag color="red">Rejected</Tag>
              <a
                style={{ color: "#ff4d4f" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete && onDelete(record);
                }}
              >
                <DeleteOutlined /> Delete
              </a>
            </div>
          );
        }

        return null;
      },
    });
  }

  const dataSource =
    dataList.length === 0
      ? []
      : dataList.map((data, index) => {
          return { ...data, key: index };
        });

  return (
    <div className="info-list-widget">
      <Table
        columns={columns}
        dataSource={dataSource}
        rowClassName={(record, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
        scroll={{ x: 1600 }}
        pagination={{ pageSize: 10 }}
      />
    </div>
  );
}

export default FacilityApplicationsTable;
